import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Save, X, Trash2, RotateCcw, Lock, Percent, Euro, AlertTriangle, Calculator } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminConfiguration({ token }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editDraft, setEditDraft] = useState({ value: '', unit: 'coefficient', description: '', name: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [newRule, setNewRule] = useState({ key: '', name: '', description: '', value: '', unit: 'coefficient' });

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Wrapped in useCallback so the useEffect below can include it as a
  // dependency without retriggering every render. Reference is stable
  // unless the auth token changes.
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/config/pricing-rules`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : [];
      if (!res.ok) throw new Error(data?.detail || 'Failed to load rules');
      setRules(data);
    } catch (e) {
      alert('Failed to load rules: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const callApi = async (path, method, body) => {
    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers: authHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });
    const raw = await res.text();
    let data = null;
    try { data = raw ? JSON.parse(raw) : null; } catch (_) {}
    if (!res.ok) throw new Error(data?.detail || raw || `HTTP ${res.status}`);
    return data;
  };

  const startEdit = (rule) => {
    setEditingKey(rule.key);
    setEditDraft({
      value: rule.value,
      unit: rule.unit,
      description: rule.description || '',
      name: rule.name,
      services: Array.isArray(rule.services) ? rule.services.join(', ') : '',
      formula: rule.formula || '',
    });
  };

  const saveEdit = async (rule) => {
    try {
      await callApi(`/api/config/pricing-rules/${encodeURIComponent(rule.key)}`, 'PATCH', {
        value: parseFloat(editDraft.value),
        unit: editDraft.unit,
        description: editDraft.description,
        name: editDraft.name,
        services: (editDraft.services || '').split(',').map((s) => s.trim()).filter(Boolean),
        formula: editDraft.formula,
      });
      setEditingKey(null);
      loadRules();
    } catch (e) { alert('Update failed: ' + e.message); }
  };

  const handleDelete = async (rule) => {
    if (rule.is_default) {
      alert('Default rules cannot be deleted. You can only edit their value, or reset them to defaults.');
      return;
    }
    if (!window.confirm(`Delete custom rule "${rule.name}"?`)) return;
    try {
      await callApi(`/api/config/pricing-rules/${encodeURIComponent(rule.key)}`, 'DELETE');
      loadRules();
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const handleAdd = async () => {
    if (!newRule.key.trim() || !newRule.name.trim()) { alert('Key and name are required'); return; }
    if (newRule.value === '' || isNaN(parseFloat(newRule.value))) { alert('Value must be a number'); return; }
    try {
      await callApi('/api/config/pricing-rules', 'POST', {
        key: newRule.key.trim(),
        name: newRule.name.trim(),
        description: newRule.description.trim(),
        value: parseFloat(newRule.value),
        unit: newRule.unit,
        category: 'custom',
      });
      setShowAdd(false);
      setNewRule({ key: '', name: '', description: '', value: '', unit: 'coefficient' });
      loadRules();
    } catch (e) { alert('Create failed: ' + e.message); }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Reset all DEFAULT rules to their original values?\n\nThis will NOT touch any custom rules you added.')) return;
    try {
      const r = await callApi('/api/config/pricing-rules/reset-defaults', 'POST');
      alert(`Reset complete. ${r.reset} rules restored.`);
      loadRules();
    } catch (e) { alert('Reset failed: ' + e.message); }
  };

  const groupedRules = rules.reduce((acc, r) => {
    const key = r.category || 'other';
    acc[key] = acc[key] || [];
    acc[key].push(r);
    return acc;
  }, {});

  const categoryMeta = {
    combo: { label: 'Combination rules (service pairs)', color: 'from-blue-500 to-cyan-500' },
    bulk_discount: { label: 'Bulk discount tiers (any services)', color: 'from-emerald-500 to-teal-500' },
    rounding: { label: 'Rounding rules', color: 'from-amber-500 to-orange-500' },
    custom: { label: 'Custom rules', color: 'from-purple-500 to-pink-500' },
    other: { label: 'Other', color: 'from-slate-500 to-slate-700' },
  };

  return (
    <div data-testid="admin-configuration">
      {/* Header + Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Calculator className="w-5 h-5 text-cyan-600" /> Pricing Rules</h2>
          <p className="text-xs text-slate-600 mt-1">Coefficients and flat amounts applied when a customer picks multiple services. Edit values freely or add your own rules.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleResetDefaults}
            data-testid="reset-defaults-btn"
            className="px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium text-sm hover:bg-amber-50 flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> Reset defaults
          </button>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-rule-btn"
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium text-sm hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add rule
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-5 mb-5" data-testid="add-rule-form">
          <h3 className="font-bold text-slate-800 mb-3">New Pricing Rule</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Key * (unique ID, e.g. rule_custom_1)</label>
              <input
                type="text"
                value={newRule.key}
                onChange={(e) => setNewRule({ ...newRule, key: e.target.value })}
                data-testid="new-rule-key"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white font-mono text-sm"
                placeholder="rule_custom_1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
              <input
                type="text"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                data-testid="new-rule-name"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
                placeholder="Название правила"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Value *</label>
              <input
                type="number"
                step="0.01"
                value={newRule.value}
                onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                data-testid="new-rule-value"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
                placeholder="0.5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Unit</label>
              <select
                value={newRule.unit}
                onChange={(e) => setNewRule({ ...newRule, unit: e.target.value })}
                data-testid="new-rule-unit"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
              >
                <option value="coefficient">Coefficient (×)</option>
                <option value="eur">Flat EUR (€)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={newRule.description}
                onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                data-testid="new-rule-description"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white text-sm"
                rows="2"
                placeholder="Когда применяется правило и как считается цена..."
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} data-testid="save-new-rule-btn" className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2">
              <Save className="w-4 h-4" /> Save rule
            </button>
            <button onClick={() => { setShowAdd(false); setNewRule({ key: '', name: '', description: '', value: '', unit: 'coefficient' }); }} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center py-10 text-slate-600">Loading rules...</p>
      ) : (
        Object.entries(groupedRules).map(([cat, list]) => {
          const meta = categoryMeta[cat] || categoryMeta.other;
          return (
            <div key={cat} className="mb-6">
              <div className={`bg-gradient-to-r ${meta.color} rounded-t-xl px-5 py-2.5 text-white`}>
                <h3 className="text-sm font-bold uppercase tracking-wider">{meta.label}</h3>
              </div>
              <div className="bg-white border border-slate-200 border-t-0 rounded-b-xl overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 text-[11px] font-bold uppercase">
                      <th className="px-3 py-2 text-left w-32">Key</th>
                      <th className="px-3 py-2 text-left">Rule</th>
                      <th className="px-3 py-2 text-center w-40">Value</th>
                      <th className="px-3 py-2 text-center w-28">Source</th>
                      <th className="px-3 py-2 text-center w-36">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid={`rules-tbody-${cat}`}>
                    {list.map((rule, idx) => {
                      const editing = editingKey === rule.key;
                      return (
                        <tr key={rule.key} className={`border-b border-slate-100 hover:bg-cyan-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`} data-testid={`rule-row-${rule.key}`}>
                          <td className="px-3 py-2 font-mono text-xs text-slate-700">{rule.key}</td>
                          <td className="px-3 py-2">
                            {editing ? (
                              <div className="space-y-1.5">
                                <input
                                  value={editDraft.name}
                                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                                  data-testid={`edit-name-${rule.key}`}
                                  className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                                <textarea
                                  value={editDraft.description}
                                  onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                                  data-testid={`edit-description-${rule.key}`}
                                  rows="2"
                                  className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                                {(rule.category === 'combo' || rule.category === 'custom') && (
                                  <input
                                    value={editDraft.services}
                                    onChange={(e) => setEditDraft({ ...editDraft, services: e.target.value })}
                                    data-testid={`edit-services-${rule.key}`}
                                    placeholder="Services keywords (comma-separated): AdBlue, DPF, ..."
                                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
                                  />
                                )}
                              </div>
                            ) : (
                              <div>
                                <p className="font-medium text-slate-800 text-sm">{rule.name}</p>
                                <p className="text-xs text-slate-600 mt-0.5">{rule.description}</p>
                                {rule.services && rule.services.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1">
                                    {rule.services.map((s) => (
                                      <span key={s} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded font-mono">{s}</span>
                                    ))}
                                  </div>
                                )}
                                {rule.formula && (
                                  <p className="text-[10px] text-slate-400 mt-1 font-mono">formula: {rule.formula}</p>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {editing ? (
                              <div className="flex gap-1 items-center justify-center">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editDraft.value}
                                  onChange={(e) => setEditDraft({ ...editDraft, value: e.target.value })}
                                  data-testid={`edit-value-${rule.key}`}
                                  className="w-20 px-2 py-1 border border-slate-300 rounded text-sm text-center font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                                <select
                                  value={editDraft.unit}
                                  onChange={(e) => setEditDraft({ ...editDraft, unit: e.target.value })}
                                  data-testid={`edit-unit-${rule.key}`}
                                  className="px-1.5 py-1 border border-slate-300 rounded text-xs bg-white"
                                >
                                  <option value="coefficient">×</option>
                                  <option value="eur">€</option>
                                </select>
                              </div>
                            ) : (
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-bold ${rule.unit === 'eur' ? 'bg-amber-100 text-amber-700' : 'bg-cyan-100 text-cyan-700'}`}>
                                {rule.unit === 'eur' ? <Euro className="w-3 h-3" /> : <Percent className="w-3 h-3" />}
                                {rule.value}
                                <span className="text-[10px] opacity-70 ml-1">{rule.unit === 'eur' ? 'EUR' : 'coef'}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {rule.is_default ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-[10px] font-bold"><Lock className="w-3 h-3" /> DEFAULT</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-bold">CUSTOM</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {editing ? (
                              <div className="flex gap-1 justify-center">
                                <button onClick={() => saveEdit(rule)} data-testid={`save-edit-${rule.key}`} className="p-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded"><Save className="w-4 h-4" /></button>
                                <button onClick={() => setEditingKey(null)} className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded"><X className="w-4 h-4" /></button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-center">
                                <button onClick={() => startEdit(rule)} data-testid={`edit-rule-${rule.key}`} className="p-1.5 bg-slate-100 hover:bg-cyan-100 text-cyan-700 rounded" title="Edit"><Edit2 className="w-4 h-4" /></button>
                                <button
                                  onClick={() => handleDelete(rule)}
                                  disabled={rule.is_default}
                                  title={rule.is_default ? 'Default rules cannot be deleted' : 'Delete custom rule'}
                                  data-testid={`delete-rule-${rule.key}`}
                                  className="p-1.5 bg-slate-100 hover:bg-red-100 text-red-600 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          );
        })
      )}

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800">
          <strong>Note:</strong> These rules are stored in the database and editable at any time. The Order wizard's price calculator will read these values to compute the final amount for combinations of services and bulk-discount tiers. If you add custom rules, reach out to have them wired into the specific service combinations you want.
        </div>
      </div>
    </div>
  );
}
